import type { AddressRange, CidrBlock, CidrValidationResult, IpCountMode, ParsedCidr } from './types';
import { isAwsSubnetAllowed } from './usableIpUtils';

const MAX_IPV4 = BigInt(0xffffffff);

export class CidrParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CidrParseError';
  }
}

/**
 * IPv4 文字列を 32bit 整数に変換する
 */
export function ipToNumber(ip: string): bigint {
  const parts = ip.trim().split('.');
  if (parts.length !== 4) {
    throw new CidrParseError('Invalid IPv4 address');
  }

  let value = BigInt(0);
  for (const part of parts) {
    if (!/^\d+$/.test(part)) {
      throw new CidrParseError('Invalid IPv4 address');
    }
    const octet = Number(part);
    if (octet < 0 || octet > 255) {
      throw new CidrParseError('Invalid IPv4 address');
    }
    value = (value << BigInt(8)) + BigInt(octet);
  }
  return value;
}

/**
 * 32bit 整数を IPv4 文字列に変換する
 */
export function numberToIp(value: bigint): string {
  const normalized = value & MAX_IPV4;
  const octets = [
    Number((normalized >> BigInt(24)) & BigInt(0xff)),
    Number((normalized >> BigInt(16)) & BigInt(0xff)),
    Number((normalized >> BigInt(8)) & BigInt(0xff)),
    Number(normalized & BigInt(0xff)),
  ];
  return octets.join('.');
}

/**
 * プレフィックス長からネットワークマスクを生成する
 */
export function prefixToMask(prefix: number): bigint {
  if (prefix < 0 || prefix > 32) {
    throw new CidrParseError('Prefix must be between 0 and 32');
  }
  if (prefix === 0) {
    return BigInt(0);
  }
  if (prefix === 32) {
    return MAX_IPV4;
  }
  return (MAX_IPV4 << BigInt(32 - prefix)) & MAX_IPV4;
}

/**
 * プレフィックス長からブロックサイズ（アドレス数）を返す
 */
export function prefixToBlockSize(prefix: number): bigint {
  if (prefix < 0 || prefix > 32) {
    throw new CidrParseError('Prefix must be between 0 and 32');
  }
  return BigInt(1) << BigInt(32 - prefix);
}

/**
 * CIDR 文字列をパースする
 */
export function parseCidr(cidr: string): ParsedCidr {
  const trimmed = cidr.trim();
  const slashIndex = trimmed.indexOf('/');
  if (slashIndex === -1) {
    throw new CidrParseError('CIDR must include a prefix length');
  }

  const ipPart = trimmed.slice(0, slashIndex);
  const prefixPart = trimmed.slice(slashIndex + 1);
  if (!/^\d+$/.test(prefixPart)) {
    throw new CidrParseError('Invalid prefix length');
  }

  const prefix = Number(prefixPart);
  if (prefix < 0 || prefix > 32) {
    throw new CidrParseError('Prefix must be between 0 and 32');
  }

  const network = ipToNumber(ipPart);
  const mask = prefixToMask(prefix);
  const normalizedNetwork = network & mask;
  const blockSize = prefixToBlockSize(prefix);
  const broadcast = normalizedNetwork + blockSize - BigInt(1);

  return {
    network: normalizedNetwork,
    prefix,
    broadcast: broadcast & MAX_IPV4,
  };
}

/**
 * CIDR をネットワークアドレス表記へ正規化する
 */
export function normalizeCidr(cidr: string): string {
  const parsed = parseCidr(cidr);
  return formatCidr(parsed.network, parsed.prefix);
}

/**
 * CIDR 文字列を検証し、必要なら正規化結果を返す
 */
export function validateCidrInput(cidr: string): CidrValidationResult {
  try {
    const trimmed = cidr.trim();
    const parsed = parseCidr(trimmed);
    const normalized = formatCidr(parsed.network, parsed.prefix);
    const inputNetwork = ipToNumber(trimmed.slice(0, trimmed.indexOf('/')));
    const wasNormalized = inputNetwork !== parsed.network;
    return { ok: true, normalized, wasNormalized };
  } catch (error) {
    const message = error instanceof CidrParseError ? error.message : 'Invalid CIDR';
    return { ok: false, error: message };
  }
}

/**
 * CIDR を文字列形式で返す
 */
export function formatCidr(network: bigint, prefix: number): string {
  return `${numberToIp(network)}/${prefix}`;
}

/**
 * CIDR のアドレス範囲を返す（開始・終了とも inclusive）
 */
export function cidrToRange(cidr: string): AddressRange {
  const parsed = parseCidr(cidr);
  return { start: parsed.network, end: parsed.broadcast };
}

/**
 * 子 CIDR が親 CIDR 内に収まるか
 */
export function fitsInParent(childCidr: string, parentCidr: string): boolean {
  const child = parseCidr(childCidr);
  const parent = parseCidr(parentCidr);
  return child.network >= parent.network && child.broadcast <= parent.broadcast;
}

/**
 * 2 つの CIDR 範囲が重複するか
 */
export function overlaps(a: AddressRange, b: AddressRange): boolean {
  return a.start <= b.end && b.start <= a.end;
}

/**
 * 2 つの CIDR が重複するか
 */
export function cidrOverlaps(aCidr: string, bCidr: string): boolean {
  return overlaps(cidrToRange(aCidr), cidrToRange(bCidr));
}

/**
 * アドレス範囲を最少の CIDR 集合に分解する
 */
export function decomposeRangeToCidrs(range: AddressRange): string[] {
  if (range.start > range.end) {
    return [];
  }

  const cidrs: string[] = [];
  let cursor = range.start;

  while (cursor <= range.end) {
    let prefix = 32;
    for (let candidate = 0; candidate <= 32; candidate += 1) {
      const blockSize = prefixToBlockSize(candidate);
      const mask = prefixToMask(candidate);
      if ((cursor & mask) !== cursor) {
        continue;
      }
      if (cursor + blockSize - BigInt(1) > range.end) {
        continue;
      }
      if (candidate < prefix) {
        prefix = candidate;
      }
    }

    cidrs.push(formatCidr(cursor, prefix));
    cursor += prefixToBlockSize(prefix);
  }

  return cidrs;
}

/**
 * 親 CIDR 内の未割り当て区間を返す
 */
export function findFreeGaps(parentCidr: string, childCidrs: string[]): AddressRange[] {
  const parent = cidrToRange(parentCidr);
  if (childCidrs.length === 0) {
    return [parent];
  }

  const sorted = childCidrs
    .map((cidr) => cidrToRange(cidr))
    .sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));

  const gaps: AddressRange[] = [];
  let cursor = parent.start;

  for (const block of sorted) {
    if (block.end < parent.start || block.start > parent.end) {
      continue;
    }
    const clampedStart = block.start < parent.start ? parent.start : block.start;
    const clampedEnd = block.end > parent.end ? parent.end : block.end;

    if (clampedStart > cursor) {
      gaps.push({ start: cursor, end: clampedStart - BigInt(1) });
    }
    if (clampedEnd + BigInt(1) > cursor) {
      cursor = clampedEnd + BigInt(1);
    }
  }

  if (cursor <= parent.end) {
    gaps.push({ start: cursor, end: parent.end });
  }

  return gaps;
}

/**
 * 空き区間の先頭から指定プレフィックスの CIDR を生成する
 */
export function splitGap(gap: AddressRange, prefix: number): string | null {
  const blockSize = prefixToBlockSize(prefix);
  const gapSize = gap.end - gap.start + BigInt(1);
  if (blockSize > gapSize) {
    return null;
  }

  const mask = prefixToMask(prefix);
  let network = gap.start & mask;
  if (network < gap.start) {
    network += blockSize;
  }

  const broadcast = network + blockSize - BigInt(1);
  if (broadcast > gap.end) {
    return null;
  }

  return formatCidr(network, prefix);
}

/**
 * CIDR が指定区間内に完全に収まるか
 */
export function fitsInRange(cidr: string, range: AddressRange): boolean {
  const block = cidrToRange(cidr);
  return block.start >= range.start && block.end <= range.end;
}

/**
 * 空き区間内へのブロック追加を検証する
 */
export function validateBlockPlacementInGap(
  candidateCidr: string,
  parentCidr: string,
  gap: AddressRange,
  existingCidrs: string[],
  ipCountMode: IpCountMode = 'standard',
): CidrValidationResult {
  const result = validateBlockPlacement(
    candidateCidr,
    parentCidr,
    existingCidrs,
    undefined,
    ipCountMode,
  );
  if (!result.ok) {
    return result;
  }

  if (!fitsInRange(result.normalized, gap)) {
    return { ok: false, error: 'OUT_OF_GAP' };
  }

  return result;
}

/**
 * ブロック追加時の検証（親内・重複なし）
 */
export function validateBlockPlacement(
  candidateCidr: string,
  parentCidr: string,
  existingCidrs: string[],
  excludeCidr?: string,
  ipCountMode: IpCountMode = 'standard',
): CidrValidationResult {
  const validation = validateCidrInput(candidateCidr);
  if (!validation.ok) {
    return validation;
  }

  if (!fitsInParent(validation.normalized, parentCidr)) {
    return { ok: false, error: 'OUT_OF_PARENT' };
  }

  const prefix = parseCidr(validation.normalized).prefix;
  if (!isAwsSubnetAllowed(prefix, ipCountMode)) {
    return { ok: false, error: 'AWS_TOO_SMALL' };
  }

  const candidateRange = cidrToRange(validation.normalized);
  for (const existing of existingCidrs) {
    if (excludeCidr && existing === excludeCidr) {
      continue;
    }
    if (overlaps(candidateRange, cidrToRange(existing))) {
      return { ok: false, error: 'OVERLAP' };
    }
  }

  return validation;
}

/**
 * IP カウントモードに合わないブロックを除外する（AWS では /29 以降を削除）
 */
export function filterBlocksForIpCountMode(blocks: CidrBlock[], mode: IpCountMode): CidrBlock[] {
  if (mode !== 'aws') {
    return blocks;
  }

  return blocks.filter((block) => {
    const { prefix } = parseCidr(block.cidr);
    return isAwsSubnetAllowed(prefix, mode);
  });
}

/**
 * 親 CIDR の使用率（0〜100）を返す
 */
export function usagePercent(parentCidr: string, childCidrs: string[]): number {
  return coveragePercent(parentCidr, childCidrs).used;
}

/**
 * 空き区間ベースで使用・空き率を返す（重複や端数の取りこぼしを反映）
 */
export function coveragePercent(
  parentCidr: string,
  childCidrs: string[],
): { used: number; free: number } {
  const parent = cidrToRange(parentCidr);
  const parentSize = parent.end - parent.start + BigInt(1);
  if (parentSize === BigInt(0)) {
    return { used: 0, free: 0 };
  }

  const gaps = findFreeGaps(parentCidr, childCidrs);
  let freeSize = BigInt(0);
  for (const gap of gaps) {
    freeSize += gap.end - gap.start + BigInt(1);
  }

  const free = Number((freeSize * BigInt(10000)) / parentSize) / 100;
  const used = Math.max(0, 100 - free);
  return { used, free };
}
